import {
  BusinessEntityTypes,
  EdgeTypes,
  EdgeSubtypes,
  NodeCategories,
  type BusinessEntityType,
  type NodeTypeConfig,
  type EdgeType,
  type EdgeSubtype,
} from './types'

import facebookIcon from '../assets/nodes/facebook.svg'
import instagramIcon from '../assets/nodes/instagram.svg'
import youtubeIcon from '../assets/nodes/youtube.svg'
import twitterIcon from '../assets/nodes/twitter.svg'
import telegramIcon from '../assets/nodes/telegram.svg'
import tiktokIcon from '../assets/nodes/tiktok.svg'
import etsyIcon from '../assets/nodes/etsy-shop.svg'
import openSeaIcon from '../assets/nodes/open-sea.svg'
import magicEdenIcon from '../assets/nodes/magic-eden.svg'
import linkedinIcon from '../assets/nodes/linkedin.svg'
import pinterestIcon from '../assets/nodes/pinterest.svg'
import websiteIcon from '../assets/nodes/website.svg'
import evmAddressIcon from '../assets/nodes/evm-address.svg'
import linktreeIcon from '../assets/nodes/linktree.svg'
import placeholderIcon from '../assets/nodes/image-placeholder.svg'
import discordIcon from '../assets/nodes/discord.svg'
import fiverrIcon from '../assets/nodes/fiverr.svg'
import foursquareIcon from '../assets/nodes/foursquare-labs.svg'
import openTableIcon from '../assets/nodes/open-table.svg'
import tripadvisorIcon from '../assets/nodes/tripadvisor.svg'
import upworkIcon from '../assets/nodes/upwork.svg'
import yelpIcon from '../assets/nodes/yelp.svg'
import phoneIcon from '../assets/nodes/phone.svg'
import emailIcon from '../assets/nodes/email.svg'
import locationIcon from '../assets/nodes/location.svg'
import shoppingCartIcon from '../assets/nodes/shopping-cart.svg'
import googleMapsIcon from '../assets/nodes/google-maps.svg'
import onlyfansIcon from '../assets/nodes/onlyfans.svg'
import airbnbHostIcon from '../assets/nodes/host.svg'
import airbnbListingIcon from '../assets/nodes/listing.svg'
import bookingHostIcon from '../assets/nodes/booking-host.svg'
import bookingListingIcon from '../assets/nodes/booking-listing.svg'
import vrboHostIcon from '../assets/nodes/vrbo-host.svg'
import vrboListingIcon from '../assets/nodes/vrbo-listing.svg'
import weedmapsIcon from '../assets/nodes/weedmaps.svg'
import barBusinessIcon from '../assets/nodes/bar-business.svg'
import buildzoomIcon from '../assets/nodes/buildzoom.svg'
import openCorporatesIcon from '../assets/nodes/open-corporates.svg'
import midragIcon from '../assets/nodes/midrag.svg'
import pandoraIcon from '../assets/nodes/pandora.svg'
import strRegistryIcon from '../assets/nodes/str-registry.svg'
import arkhamIcon from '../assets/nodes/arkham.svg'
import blurCollectionIcon from '../assets/nodes/blur-collection.svg'
import ensDomainIcon from '../assets/nodes/ens-domain.svg'
import ensProfileIcon from '../assets/nodes/ens-profile.svg'
import farcasterIcon from '../assets/nodes/farcaster.svg'
import web3BioIcon from '../assets/nodes/web3-bio.svg'

/**
 * Unified node type configuration - Single source of truth for each business entity type
 * Contains category, icon, and label in one place for easier maintenance
 */
export const NODE_TYPE_CONFIG: Record<BusinessEntityType, NodeTypeConfig> = {
  // Data source nodes
  WEBSITE: { category: NodeCategories.DATA_SOURCE, icon: websiteIcon, label: 'Website' },
  FACEBOOK: { category: NodeCategories.DATA_SOURCE, icon: facebookIcon, label: 'Facebook' },
  INSTAGRAM: { category: NodeCategories.DATA_SOURCE, icon: instagramIcon, label: 'Instagram' },
  YOUTUBE_CHANNEL: { category: NodeCategories.DATA_SOURCE, icon: youtubeIcon, label: 'YouTube Channel' },
  TWITTER: { category: NodeCategories.DATA_SOURCE, icon: twitterIcon, label: 'Twitter' },
  TELEGRAM: { category: NodeCategories.DATA_SOURCE, icon: telegramIcon, label: 'Telegram' },
  TIKTOK_PROFILE: { category: NodeCategories.DATA_SOURCE, icon: tiktokIcon, label: 'TikTok' },
  ETSY_SHOP: { category: NodeCategories.DATA_SOURCE, icon: etsyIcon, label: 'Etsy Shop' },
  ETSY_PEOPLE: { category: NodeCategories.DATA_SOURCE, icon: etsyIcon, label: 'Etsy People' },
  OPENSEA_ACCOUNT: { category: NodeCategories.DATA_SOURCE, icon: openSeaIcon, label: 'OpenSea Account' },
  OPENSEA_COLLECTION: { category: NodeCategories.DATA_SOURCE, icon: openSeaIcon, label: 'OpenSea Collection' },
  MAGIC_EDEN_ACCOUNT: { category: NodeCategories.DATA_SOURCE, icon: magicEdenIcon, label: 'Magic Eden Account' },
  MAGIC_EDEN_COLLECTION: { category: NodeCategories.DATA_SOURCE, icon: magicEdenIcon, label: 'Magic Eden Collection' },
  GMAPS: { category: NodeCategories.DATA_SOURCE, icon: googleMapsIcon, label: 'Google Maps' },
  EVM_ADDRESS: { category: NodeCategories.DATA_SOURCE, icon: evmAddressIcon, label: 'EVM Address' },
  BLOCKCHAIN_WALLET: { category: NodeCategories.DATA_SOURCE, icon: evmAddressIcon, label: 'Blockchain Wallet' },
  LINKEDIN: { category: NodeCategories.DATA_SOURCE, icon: linkedinIcon, label: 'LinkedIn' },
  LINKEDIN_PROFILE: { category: NodeCategories.DATA_SOURCE, icon: linkedinIcon, label: 'LinkedIn Profile' },
  LINKEDIN_COMPANY: { category: NodeCategories.DATA_SOURCE, icon: linkedinIcon, label: 'LinkedIn Company' },
  ONLYFANS: { category: NodeCategories.DATA_SOURCE, icon: onlyfansIcon, label: 'OnlyFans' },
  PINTEREST_PROFILE: { category: NodeCategories.DATA_SOURCE, icon: pinterestIcon, label: 'Pinterest' },
  AIRBNB_HOST: { category: NodeCategories.DATA_SOURCE, icon: airbnbHostIcon, label: 'Airbnb Host' },
  AIRBNB_LISTING: { category: NodeCategories.DATA_SOURCE, icon: airbnbListingIcon, label: 'Airbnb Listing' },
  BOOKING_HOST: { category: NodeCategories.DATA_SOURCE, icon: bookingHostIcon, label: 'Booking Host' },
  BOOKING_LISTING: { category: NodeCategories.DATA_SOURCE, icon: bookingListingIcon, label: 'Booking Listing' },
  LINKTREE: { category: NodeCategories.DATA_SOURCE, icon: linktreeIcon, label: 'Linktree' },
  SHOPIFY_SHOP: { category: NodeCategories.DATA_SOURCE, icon: shoppingCartIcon, label: 'Shopify Shop' },
  ECOMMERCE_SHOP: { category: NodeCategories.DATA_SOURCE, icon: shoppingCartIcon, label: 'E-commerce Shop' },
  OPENTABLE: { category: NodeCategories.DATA_SOURCE, icon: openTableIcon, label: 'OpenTable' },
  YELP_BUSINESS: { category: NodeCategories.DATA_SOURCE, icon: yelpIcon, label: 'Yelp Business' },
  TRIPADVISOR_BUSINESS: { category: NodeCategories.DATA_SOURCE, icon: tripadvisorIcon, label: 'TripAdvisor Business' },
  WEEDMAPS_BUSINESS: { category: NodeCategories.DATA_SOURCE, icon: weedmapsIcon, label: 'Weedmaps Business' },
  FOURSQUARE_BUSINESS: { category: NodeCategories.DATA_SOURCE, icon: foursquareIcon, label: 'Foursquare Business' },
  BAR_BUSINESS: { category: NodeCategories.DATA_SOURCE, icon: barBusinessIcon, label: 'Bar Business' },
  FIVERR_PROFILE: { category: NodeCategories.DATA_SOURCE, icon: fiverrIcon, label: 'Fiverr Profile' },
  UPWORK_PROFILE: { category: NodeCategories.DATA_SOURCE, icon: upworkIcon, label: 'Upwork Profile' },
  BUILDZOOM: { category: NodeCategories.DATA_SOURCE, icon: buildzoomIcon, label: 'BuildZoom' },
  DISCORD_GUILD: { category: NodeCategories.DATA_SOURCE, icon: discordIcon, label: 'Discord' },
  DISCORD_INVITE: { category: NodeCategories.DATA_SOURCE, icon: discordIcon, label: 'Discord Invite' },
  OPENCORPORATES: { category: NodeCategories.DATA_SOURCE, icon: openCorporatesIcon, label: 'OpenCorporates' },
  MIDRAG: { category: NodeCategories.DATA_SOURCE, icon: midragIcon, label: 'Midrag' },
  VRBO_HOST: { category: NodeCategories.DATA_SOURCE, icon: vrboHostIcon, label: 'Vrbo Host' },
  VRBO_LISTING: { category: NodeCategories.DATA_SOURCE, icon: vrboListingIcon, label: 'Vrbo Listing' },
  MANUAL_LEAD: { category: NodeCategories.DATA_SOURCE, icon: websiteIcon, label: 'Manual Lead' },
  PANDORA_PAPERS_OFFICER: { category: NodeCategories.DATA_SOURCE, icon: pandoraIcon, label: 'Pandora Papers Officer' },
  CRS_TAX_PAYER: { category: NodeCategories.DATA_SOURCE, icon: placeholderIcon, label: 'CRS Tax Payer' },
  REGISTRY_OFFICER_CY: { category: NodeCategories.DATA_SOURCE, icon: placeholderIcon, label: 'Registry Officer CY' },
  REGISTRY_ORGANIZATION_CY: {
    category: NodeCategories.DATA_SOURCE,
    icon: placeholderIcon,
    label: 'Registry Organization CY',
  },
  STR_REGISTRY: { category: NodeCategories.DATA_SOURCE, icon: strRegistryIcon, label: 'STR Registry' },
  STR_REGISTRY_GIS: { category: NodeCategories.DATA_SOURCE, icon: strRegistryIcon, label: 'STR Registry GIS' },
  ARKHAM: { category: NodeCategories.DATA_SOURCE, icon: arkhamIcon, label: 'Arkham' },
  BLUR_COLLECTION: { category: NodeCategories.DATA_SOURCE, icon: blurCollectionIcon, label: 'Blur Collection' },
  ENS_DOMAIN: { category: NodeCategories.DATA_SOURCE, icon: ensDomainIcon, label: 'ENS Domain' },
  ENS_PROFILE: { category: NodeCategories.DATA_SOURCE, icon: ensProfileIcon, label: 'ENS Profile' },
  FARCASTER: { category: NodeCategories.DATA_SOURCE, icon: farcasterIcon, label: 'Farcaster' },
  WEB3_BIO: { category: NodeCategories.DATA_SOURCE, icon: web3BioIcon, label: 'Web3 Bio' },
  // Contact info nodes
  PHONE: { category: NodeCategories.CONTACT_INFO, icon: phoneIcon, label: 'Phone' },
  LOCATION: { category: NodeCategories.CONTACT_INFO, icon: locationIcon, label: 'Location' },
  EMAIL: { category: NodeCategories.CONTACT_INFO, icon: emailIcon, label: 'Email' },
  // Identity nodes
  IDENTITY: { category: NodeCategories.IDENTITY, icon: null, label: 'Identity' },
}

const UNKNOWN_NODE_CONFIG: NodeTypeConfig = {
  category: NodeCategories.UNKNOWN,
  icon: placeholderIcon,
  label: 'Unknown',
}

/** Safe accessor that returns a fallback config for unknown business entity types */
export function getNodeTypeConfig(type: string): NodeTypeConfig {
  return NODE_TYPE_CONFIG[type as BusinessEntityType] ?? UNKNOWN_NODE_CONFIG
}

/**
 * Node type options for dropdowns/selects
 */
export const NODE_TYPE_OPTIONS = Object.entries(BusinessEntityTypes).map(([_, value]) => ({
  value,
  label: getNodeTypeConfig(value).label,
}))

/**
 * Human-readable labels for edge types
 */
export const EDGE_TYPE_LABELS: Record<EdgeType, string> = {
  [EdgeTypes.INCLUDED]: 'Included',
  [EdgeTypes.INFERRED]: 'Inferred',
}

/**
 * Human-readable labels for edge subtypes
 */
export const EDGE_SUBTYPE_LABELS: Record<EdgeSubtype, string> = {
  [EdgeSubtypes.IS_LINKED]: 'Is Linked',
  [EdgeSubtypes.IDENTITY_DATA]: 'Identity Data',
  [EdgeSubtypes.IDENTICAL_PHOTO]: 'Identical Photo',
  [EdgeSubtypes.IDENTICAL_USERNAME]: 'Identical Username',
  [EdgeSubtypes.SIMILAR_PROFILE_INFO]: 'Similar Profile Info',
  [EdgeSubtypes.SOCIAL_NETWORK_OVERLAP]: 'Social Network Overlap',
  [EdgeSubtypes.OVERLAPPING_GEOTAGGED_LOCATIONS]: 'Overlapping Geotagged Locations',
  [EdgeSubtypes.RECURRING_CROSS_PLATFORM_LOCATIONS]: 'Recurring Cross-Platform Locations',
  [EdgeSubtypes.OPEN_SOURCE_MATCHING]: 'Open Source Matching',
  [EdgeSubtypes.SAME_DOMAIN]: 'Same Domain',
  [EdgeSubtypes.SAME_OWNER_NAME]: 'Same Owner Name',
  [EdgeSubtypes.PUBLIC_LOCATION]: 'Public Location',
  // Crypto - ENS and wallet relationships
  [EdgeSubtypes.SHARED_ENS_NAME_WALLET]: 'Shared ENS Name Wallet',
  [EdgeSubtypes.INFERRED_SAME_ENTITY]: 'Inferred Same Entity',
  [EdgeSubtypes.ONE_SIDED_TRANSFERS]: 'One-Sided Transfers',
  [EdgeSubtypes.TOP_COUNTERPARTIES]: 'Top Counterparties',
  [EdgeSubtypes.ROYALTY_WALLET]: 'Royalty Wallet',
  [EdgeSubtypes.DEPOSIT_WALLET]: 'Deposit Wallet',
  [EdgeSubtypes.FUNDED_BY_WALLET]: 'Funded-By Wallet',
  [EdgeSubtypes.CONTRACT_CREATOR]: 'Contract Creator',
  [EdgeSubtypes.PUBLISHED_PROFILE_ADDRESS]: 'Published Profile Address',
  [EdgeSubtypes.PROFILE_PICTURE_NFT_OWNER]: 'Profile Picture NFT Owner',
  [EdgeSubtypes.CIRCULAR_TRADE_PATTERN]: 'Circular Trade Pattern',
  // E-commerce / Etsy - company and website relationships
  [EdgeSubtypes.PARENT_COMPANY_WEBSITE]: 'Parent Company Website',
  [EdgeSubtypes.SUBSIDIARY_COMPANY_WEBSITE]: 'Subsidiary Company Website',
  [EdgeSubtypes.SISTER_COMPANY_WEBSITE]: 'Sister Company Website',
  [EdgeSubtypes.SAME_LOGO]: 'Same Logo',
}

/**
 * Animation duration for viewport transitions (in milliseconds)
 */
export const FIT_VIEW_DURATION = 500

/**
 * Node size for identity nodes (square dimensions in pixels)
 */
export const IDENTITY_NODE_SIZE = 85
